/* eslint-disable no-unused-vars */
type PdfLinkDocument = {
  link: (...args: [number, number, number, number, { url: string }]) => void;
};
/* eslint-enable no-unused-vars */

type AddPdfLinkAnnotationsParams = {
  doc: PdfLinkDocument;
  pageElement: HTMLElement;
  pageX: number;
  pageY: number;
  pageWidth: number;
  pageHeight: number;
};

type ClampParams = {
  value: number;
  min: number;
  max: number;
};

type ElementParam = {
  element: HTMLElement;
};

const clamp = ({ value, min, max }: ClampParams) => Math.min(Math.max(value, min), max);

const getPdfHref = ({ element }: ElementParam) => element.getAttribute("data-href")?.trim() || "";

// html2pdf snapshots the report card itself, not the full outer page wrapper.
// Use the same visual bounds for link math or annotations drift away from text.
const getCapturedElement = ({ element }: ElementParam) =>
  element.querySelector<HTMLElement>(".report-wrapper") ?? element;

/**
 * Adds real PDF link annotations over image-rendered report links.
 *
 * Report downloads are generated by html2canvas/html2pdf as page images and then
 * inserted into jsPDF with `addImage`. That preserves visual fidelity, but DOM
 * links and click handlers are flattened into pixels. Chrome may auto-detect
 * some URL-looking text, but that behavior is viewer-dependent and unreliable,
 * especially for wrapped URLs.
 *
 * Any visible report element with `data-href` gets one `doc.link` annotation per
 * client rectangle, so wrapped links remain clickable across all lines. The
 * annotation coordinates are scaled from browser pixels into the jsPDF page
 * dimensions used for the inserted image.
 */
export const addPdfLinkAnnotations = ({
  doc,
  pageElement,
  pageX,
  pageY,
  pageWidth,
  pageHeight,
}: AddPdfLinkAnnotationsParams) => {
  const capturedElement = getCapturedElement({ element: pageElement });
  const pageRect = capturedElement.getBoundingClientRect();

  if (pageRect.width <= 0 || pageRect.height <= 0) {
    return;
  }

  const xScale = pageWidth / pageRect.width;
  const yScale = pageHeight / pageRect.height;
  const linkElements = Array.from(pageElement.querySelectorAll<HTMLElement>("[data-href]"));

  linkElements.forEach((linkElement) => {
    const href = getPdfHref({ element: linkElement });
    if (!href) {
      return;
    }

    const rects = Array.from(linkElement.getClientRects());

    rects.forEach((rect) => {
      const left = clamp({ value: rect.left - pageRect.left, min: 0, max: pageRect.width });
      const top = clamp({ value: rect.top - pageRect.top, min: 0, max: pageRect.height });
      const right = clamp({ value: rect.right - pageRect.left, min: 0, max: pageRect.width });
      const bottom = clamp({ value: rect.bottom - pageRect.top, min: 0, max: pageRect.height });
      const width = right - left;
      const height = bottom - top;

      if (width <= 0 || height <= 0) {
        return;
      }

      doc.link(
        pageX + (left * xScale),
        pageY + (top * yScale),
        width * xScale,
        height * yScale,
        { url: href }
      );
    });
  });
};
